import { Nutrients, FoodMeasure } from './data-model/food';

// The model registry now lives in the shared scan-models.ts (one registry
// for all scanners). See design-docs/scanner-model-config.md.

// ── Nutrient validation ────────────────────────────────────────────

// a real nutrition label has at least 3 of: energy, protein, fat, carbs
export const CORE_NUTRIENT_TAGS = ['ENER$_kcal', 'ENER$_kJ', 'PRO$_g', 'FAT_g', 'CHOCDF_g', 'CHOAVL_g'];

export function hasPartialNutrients(nutrients: Nutrients): boolean {
  const coreTags = new Set(CORE_NUTRIENT_TAGS);
  const coreCount = Object.keys(nutrients).filter(tag => coreTags.has(tag)).length;
  return coreCount < 3;
}

// ── Client → Server params ──────────────────────────────────────────

export interface ScanLabelParams {
  imageBase64?: string;       // base64-encoded image (JPEG) — single image (backward compat)
  imagesBase64?: string[];    // base64-encoded images (JPEG) — multi-photo (preferred)
  model?: string;             // a model key from SCAN_MODELS, or 'all'
  // The user's active UI language (#4206). The server writes its OWN generated
  // prose — here, the `description` field — in this language, and never
  // translates text read off the packaging (foodName / brandName / ingredients
  // stay exactly as printed). Optional: an absent or unsupported code falls back
  // to English, so an older client that omits it behaves as before.
  locale?: string;
}

// ── Server → Client results ─────────────────────────────────────────

export interface ScanLabelModelResult {
  nutrients?: Nutrients;
  measures?: FoodMeasure[];
  referenceWeight_g?: number;
  rawText?: string;            // a sub-field the model may emit INSIDE its JSON (not the whole response)
  rawResponse?: string;        // the complete raw text the model returned, before our JSON parsing — kept for post-hoc debugging
  labelStyle?: 'US' | 'EU';   // detected label regulatory style
  confidence?: number;         // 0-1 overall confidence in extraction
  nutrientConfidences?: { [key: string]: number }; // per-nutrient confidence 0-1
  jsonRepaired?: boolean;      // true if the LLM response was truncated and had to be patched
  foodName?: string;           // product name extracted from packaging
  brandName?: string;          // brand/manufacturer name
  ingredients?: string;        // full ingredients text as printed
  description?: string;        // brief description of the food
  representativeImageIndex?: number;  // 0-based index of best branding photo
  cropRect?: { x: number; y: number; w: number; h: number };  // normalized 0-1 crop rectangle
  barcode?: string;              // EAN/UPC barcode digits if visible
  noNutrientsReason?: 'blurry' | 'no_label';  // why nutrients are empty (LLM-reported)
  durationMs?: number;
  inputTokens?: number;
  outputTokens?: number;
  error?: string;
}

export interface ScanLabelResults {
  // single-model response
  nutrients?: Nutrients;
  measures?: FoodMeasure[];
  referenceWeight_g?: number;
  rawText?: string;
  modelKey?: string;
  labelStyle?: 'US' | 'EU';
  confidence?: number;
  nutrientConfidences?: { [key: string]: number };
  jsonRepaired?: boolean;
  foodName?: string;
  brandName?: string;
  ingredients?: string;
  description?: string;
  representativeImageIndex?: number;
  cropRect?: { x: number; y: number; w: number; h: number };
  barcode?: string;
  noNutrientsReason?: 'blurry' | 'no_label';
  error?: string;

  // multi-model response (model='all')
  modelResults?: { [modelKey: string]: ScanLabelModelResult };
  scanId?: string;           // for logLabelScanChoice callback

  // quota info (piggybacks on scan response)
  quota?: ScanLabelQuota;
}

// ── Scan errors ───────────────────────────────────────────────────

export enum ScanLabelError {
  AuthRequired = 'auth_required',
  NoImage = 'no_image',
  TooManyImages = 'too_many_images',
  ImageTooLarge = 'image_too_large',
  AllModelsRestricted = 'all_models_restricted',
  UnknownModel = 'unknown_model',
  ProviderError = 'provider_error',
  ProviderTimeout = 'provider_timeout',
  // the model stopped before returning a complete response — typically the output-token
  // budget was exhausted (a thinking model spent it reasoning). Transient: retrying the
  // same images usually succeeds, so the client keeps the captured photos.
  IncompleteResponse = 'incomplete_response',
}

// ── Quota / throttling ────────────────────────────────────────────

export type LabelScanStatus = 'success' | 'blurry' | 'no_label' | 'no_food' | 'redundant';

export enum ScanQuotaRejection {
  AccountLocked = 'account_locked',
  RateLimited = 'rate_limited',
  DailyCooldown = 'daily_cooldown',
  QualityWarning = 'quality_warning',
  // the whole app's daily AI-scan ceiling was reached, not this user's own quota (#4032)
  GlobalCap = 'global_cap',
}

export interface ScanLabelQuota {
  scansToday: number;
  rejection?: ScanQuotaRejection;  // why the scan was rejected or warned
  cooldownUntil_utc?: string;      // day cooldown (midnight UTC)
  lockedUntil_utc?: string;        // account lock (1 month)
}

// ── Analytics choice callback ───────────────────────────────────────

export interface LogLabelScanChoiceParams {
  scanId: string;
  pickedModel: string;       // model key the user accepted
}
